import { XMarkIcon } from '@heroicons/react/24/outline';
import { Candidate } from '../../Models';

interface GradeMetricsModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidate: Candidate;
  type: 'combined' | 'gap';
}

const GradeMetricsModal: React.FC<GradeMetricsModalProps> = ({
  isOpen,
  onClose,
  candidate,
  type
}) => {
  if (!isOpen) return null;

  const renderCombinedGradeMetrics = () => (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
            Overall CV Grade
          </h4>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {candidate.combinedGrade?.toFixed(2) || 'N/A'}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Percentile: {candidate.combinedGradePercentile?.toFixed(2) || 'N/A'}%ile
          </p>
        </div>

        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
          <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
            CV Grade (Age Based)
          </h4>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {candidate.combinedPercentileBasedAge?.toFixed(2) || 'N/A'}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            %ile
          </p>
        </div>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
            CV Grade (Exp Based)
          </h4>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {candidate.combinePercentileBasedEXP?.toFixed(2) || 'N/A'}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            %ile
          </p>
        </div>

        <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
          <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
            Education Grade
          </h4>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {candidate.totalEducationGrade?.toFixed(2) || 'N/A'}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Percentile: {candidate.educationPercentileSameRole?.toFixed(2) || 'N/A'}%ile
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Education Grade (Age Based)
            </span>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {candidate.educationPercentileBasedAge?.toFixed(2) || 'N/A'}%ile
            </span>
          </div>
        </div>

        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Education Grade (Exp Based)
            </span>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {candidate.educationPercentileBasedEXP?.toFixed(2) || 'N/A'}%ile
            </span>
          </div>
        </div>

        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Work Experience Grade
            </span>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {candidate.totalExperienceGrade?.toFixed(2) || 'N/A'} ({candidate.experiencePercentileSameRole?.toFixed(2) || 'N/A'}%ile)
            </span>
          </div>
        </div>

        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Experience Grade (Age Based)
            </span>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {candidate.experiencePercentileBasedAge?.toFixed(2) || 'N/A'}%ile
            </span>
          </div>
        </div>

        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Experience Grade (Exp Based)
            </span>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {candidate.experiencePercentileBasedEXP?.toFixed(2) || 'N/A'}%ile
            </span>
          </div>
        </div>

        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Experience in Months
            </span>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {candidate.experienceInMonths || 0} months ({candidate.experiencePercentileMonths?.toFixed(2) || 'N/A'}%ile)
            </span>
          </div>
        </div>
      </div>
    </>
  );

  const renderGapGradeMetrics = () => (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
            Total Gap
          </h4>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {candidate.totalGapMonths || 0}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            months
          </p>
        </div>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
          <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
            Gap Percentile
          </h4>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {candidate.gappercentile?.toFixed(2) || 'N/A'}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            %ile
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Gap Grade (Age Based)
            </span>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {candidate.gapmonthspercentileAge?.toFixed(2) || 'N/A'}%ile
            </span>
          </div>
        </div>

        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Gap Grade (Exp Based)
            </span>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {candidate.gapPercentileBasedEXP?.toFixed(2) || 'N/A'}%ile
            </span>
          </div>
        </div>

        {/* <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Age
            </span>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {candidate.age ? Math.floor(candidate.age / 12) : 'N/A'} years
            </span>
          </div>
        </div> */}

        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Age Percentile
            </span>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {candidate.agePercentile?.toFixed(2) || 'N/A'}%ile
            </span>
          </div>
        </div>

        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Experience (Same Age)
            </span>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {candidate.expPercentileforsameage?.toFixed(2) || 'N/A'}%ile
            </span>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />
        <div className="relative w-full max-w-2xl bg-white dark:bg-darkbglight rounded-lg shadow-xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-300">
              {type === 'combined' ? 'Combined Grade Breakdown' : 'Gap Grade Details'}
            </h3>
          </div>
          
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {type === 'combined' ? renderCombinedGradeMetrics() : renderGapGradeMetrics()}
          </div>

          <div className="flex items-center justify-end p-4 border-t border-gray-200 dark:border-gray-800">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GradeMetricsModal;